import type { ValidatorRule } from 'rc-field-form/lib/interface';

/**
 * Creates an Antd Form item generator for restricted special characters when
 * used in a Okta group name.
 *
 * Currently restricts:
 * - "
 * - \
 *
 * @param errorMessage - Human readable error to be displayed in the UI
 * @returns validator for Antd
 */
export const validatorGroupComponentRestrictedSpecialCharacters =
  <T extends string>(errorMessage: string): ValidatorRule['validator'] =>
  (_, value: T) =>
    /("|\\)/gu.test(value)
      ? Promise.reject(new Error(errorMessage))
      : Promise.resolve();

/**
 * Creates an Antd Form item generator for min length of a string
 * (min-inclusive)
 *
 * @param errorMessage - Human readable error to be displayed in the UI
 * @param minLength - Minimum length of a string
 * @returns validator for Antd
 */
export const validatorMinLength =
  <T extends string>(
    errorMessage: string,
    minLength: number
  ): ValidatorRule['validator'] =>
  (_, value?: T) =>
    (value?.length ?? 0) >= minLength
      ? Promise.resolve()
      : Promise.reject(new Error(errorMessage));

/**
 * Creates an Antd Form item generator for max length of a string
 * (max-inclusive)
 *
 * @param errorMessage - Human readable error to be displayed in the UI
 * @param maxLength - Maximum length of a string
 * @returns validator for Antd
 */
export const validatorMaxLength =
  <T extends string>(
    errorMessage: string,
    maxLength: number
  ): ValidatorRule['validator'] =>
  (_, value?: T) =>
    (value?.length ?? 0) <= maxLength
      ? Promise.resolve()
      : Promise.reject(new Error(errorMessage));

/**
 * Creates an Antd Form item generator to ensure no whitespace on the start or
 * end of a string
 *
 * @param errorMessage - Human readable error to be displayed in the UI
 * @returns validator for Antd
 */
export const validatorNoWhiteSpaceOnEnds =
  <T extends string>(errorMessage: string): ValidatorRule['validator'] =>
  (_, value?: T) =>
    value?.trim().length === value?.length
      ? Promise.resolve()
      : Promise.reject(new Error(errorMessage));
